// src/pages/PaymentSuccessPage.tsx
import React, { useEffect, useState } from 'react';
import {
  useSearchParams,
  useNavigate,
  useLocation
} from 'react-router-dom';
import apiClient from '../api/config';
import { useTranslation } from 'react-i18next';

import logo  from '../assets/damelio_logo_negro.png';
import logo2 from '../assets/damelio_texto_negro.png';

interface LocationState {
  paymentHandled?: boolean;
  msg?: string;
}

const PaymentSuccessPage: React.FC = () => {
  const { t }      = useTranslation();
  const navigate   = useNavigate();
  const location   = useLocation();
  // Si location.state es null usamos {} como fallback
  const locState   = (location.state as LocationState) || {};

  const [params]   = useSearchParams();
  const [msg, setMsg] = useState<string>(
    locState.msg ?? t('paymentSuccess.confirming')
  );

  const sessionId  = params.get('session_id');
  const orderToken = params.get('token');
  const kind       = params.get('type');

  useEffect(() => {
    // Si ya confirmamos antes, no volvemos a llamar
    if (locState.paymentHandled) return;

    const confirm = async () => {
      let finalMsg = t('paymentSuccess.error');

      try {
        if (sessionId) {
          const resp = await apiClient.get<{ status: number }>(
            `/payments/confirm?session_id=${sessionId}`
          );
          finalMsg =
            resp.status === 200
              ? `${t('paymentSuccess.confirmedPrefix')}\n${t('paymentSuccess.confirmedSuffix')}`
              : t('paymentSuccess.notConfirmed');

        } else if (orderToken) {
          const url =
            kind === 'tokens'
              ? '/payments/paypal/tokens/capture-order'
              : '/payments/paypal/capture-order';

          const { status, data } = await apiClient.post<{ status: 'paid' }>(
            url,
            { order_id: orderToken }
          );
          finalMsg =
            status === 200 && data.status === 'paid'
              ? `${t('paymentSuccess.confirmedPrefix')}\n${t('paymentSuccess.confirmedSuffix')}`
              : t('paymentSuccess.notConfirmed');

        } else {
          finalMsg = t('paymentSuccess.noParam');
        }
      } catch (err) {
        console.error(err);
        finalMsg = t('paymentSuccess.error');
      } finally {
        // Guardamos en el state de React Router que ya confirmamos
        navigate('/payment-success', {
          replace: true,
          state: {
            paymentHandled: true,
            msg: finalMsg
          }
        });
        setMsg(finalMsg);
      }
    };

    confirm();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, orderToken, kind]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-sm w-full bg-white p-6 rounded shadow space-y-6">
        <div
          className="text-center cursor-pointer"
          onClick={() => navigate('/dashboard')}
        >
          <img src={logo}  alt="Damelio" className="mx-auto h-32" />
          <img src={logo2} alt="Damelio" className="mx-auto h-16 mt-2" />
        </div>

        <h2 className="text-center text-2xl font-bold">
          {t('paymentSuccess.title')}
        </h2>

        <p className="whitespace-pre-line text-center text-gray-700">{msg}</p>

        <div className="text-center mt-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded shadow"
          >
            {t('paymentSuccess.goDashboard')}
          </button>
          <p className="text-sm text-gray-600 mt-2">
            {t('paymentSuccess.extraInfo')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
